/**
 * Team Detail component
 * Shows team sidebar (Membros | Instrumentos) with full-page views
 * Uses web-design reference: dark theme, sidebar nav, Playfair headings
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  removeMember,
  deleteTeam,
  getTeamMembers,
  getTeamRoles,
  Team,
  TeamMember,
  TeamRole,
} from '../../../api/teams';
import { listMemberInstruments } from '../../../api/instruments';
import { useTeamStore } from '../../../stores/teamStore';
import { useAuthStore } from '../../../stores/authStore';
import type { MemberInstrument } from '../../../types/instrument';
import TeamSidebar from './TeamSidebar';
import TeamMembersView from './TeamMembersView';
import InstrumentCatalog from './InstrumentCatalog';
import RolesView from './RolesView';

interface Props {
  team: Team;
  onBack?: () => void;
}

export default function TeamDetail({ team, onBack }: Props) {
  const navigate = useNavigate();
  const { loadTeams } = useTeamStore();
  const { user } = useAuthStore();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [teamRoles, setTeamRoles] = useState<TeamRole[]>([]);
  const [memberInstruments, setMemberInstruments] = useState<Record<string, MemberInstrument[]>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'members' | 'instruments' | 'roles'>('members');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showInviteLink, setShowInviteLink] = useState(false);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);

  const loadMembers = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getTeamMembers(team.id);
      setMembers(data);
    } catch { /* ignore */ } finally {
      setIsLoading(false);
    }
  }, [team.id]);

  const loadTeamRoles = useCallback(async () => {
    try {
      setTeamRoles(await getTeamRoles(team.id));
    } catch {
      setTeamRoles([]);
    }
  }, [team.id]);

  const loadAllMemberInstruments = useCallback(async () => {
    if (members.length === 0) return;
    const results: Record<string, MemberInstrument[]> = {};
    await Promise.all(
      members.map(async (m) => {
        try {
          results[m.user_id] = await listMemberInstruments(team.ministry_id, m.user_id);
        } catch {
          results[m.user_id] = [];
        }
      })
    );
    setMemberInstruments(results);
  }, [team.ministry_id, members]);

  useEffect(() => {
    loadMembers();
  }, [loadMembers]);

  useEffect(() => {
    loadTeamRoles();
  }, [loadTeamRoles]);

  useEffect(() => {
    loadAllMemberInstruments();
  }, [loadAllMemberInstruments]);

  const handleCopyInviteLink = async () => {
    const link = `${window.location.origin}/teams/join/${team.invite_code}`;
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = link;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopiedToClipboard(true);
      setTimeout(() => setCopiedToClipboard(false), 2000);
    } catch { /* ignore */ }
  };

  const handleDeleteTeam = async () => {
    if (!confirm('Tem certeza que deseja deletar esta equipe? Esta ação é irreversível.')) return;
    setIsDeleting(true);
    try {
      await deleteTeam(team.id);
      await loadTeams();
      navigate('/teams');
    } catch {
      setIsDeleting(false);
    }
  };

  const currentUserMembership = members.find((m) => m.user_id === user?.id);
  const isUserAdmin = currentUserMembership?.role === 'ADMIN';
  const isUserAdminOrLeader = currentUserMembership?.role === 'ADMIN' || currentUserMembership?.role === 'LEADER';
  const inviteLink = `${window.location.origin}/teams/join/${team.invite_code}`;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-white/30 text-sm">Carregando...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-w-0 max-w-full bg-[#0A0A0F] border border-white/5 rounded-xl overflow-hidden">
      {/* Top Bar: back + team name + invite */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/5 bg-[#0D0C09]">
        <div className="flex items-center gap-3 min-w-0">
          {onBack && (
            <button
              onClick={onBack}
              className="p-1.5 rounded-lg hover:bg-white/5 transition-colors text-white/50 hover:text-white flex-shrink-0"
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
          )}
          <h2
            className="text-white font-semibold text-base truncate"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {team.name}
          </h2>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setShowInviteLink(!showInviteLink)}
            className="text-xs px-3 py-1.5 rounded-lg bg-white/5 border border-white/5 text-white/50 hover:text-white hover:border-white/20 transition-colors"
          >
            <i className="ri-link text-xs mr-1"></i>
            Convite
          </button>
          {isUserAdmin && (
            <button
              onClick={handleDeleteTeam}
              disabled={isDeleting}
              className="text-xs px-3 py-1.5 rounded-lg text-red-400/60 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-30"
            >
              {isDeleting ? '...' : 'Excluir'}
            </button>
          )}
        </div>
      </div>

      {/* Invite Link Banner */}
      {showInviteLink && (
        <div className="mx-5 my-3 bg-[#C8A96E]/10 border border-[#C8A96E]/30 rounded-lg p-4">
          <p className="text-white/70 text-xs mb-2">Compartilhe para convidar membros:</p>
          <div className="flex gap-2">
            <code className="flex-1 bg-white/5 border border-white/10 px-3 py-2 rounded text-white text-xs font-mono overflow-x-auto break-all">
              {inviteLink}
            </code>
            <button
              onClick={handleCopyInviteLink}
              className="px-4 py-2 bg-[#C8A96E] text-[#0A0A0F] rounded-lg hover:bg-[#D4B87A] transition-colors font-semibold text-xs whitespace-nowrap"
            >
              {copiedToClipboard ? '✓' : 'Copiar'}
            </button>
          </div>
        </div>
      )}

      {/* Sidebar + Content */}
      <div className="flex flex-col md:flex-row flex-1 min-h-0 overflow-hidden">
        <TeamSidebar
          activeTab={activeTab}
          onTabChange={setActiveTab}
          teamName={team.name}
        />

        {/* Main content area */}
        {activeTab === 'members' && (
          <TeamMembersView
            teamId={team.id}
            ministryId={team.ministry_id}
            members={members}
            teamRoles={teamRoles}
            memberInstruments={memberInstruments}
            isAdmin={isUserAdmin ?? false}
            onMembersChange={loadMembers}
            onMemberInstrumentsChange={loadAllMemberInstruments}
          />
        )}

        {activeTab === 'instruments' && (
          <InstrumentCatalog
            ministryId={team.ministry_id}
            isAdmin={isUserAdmin ?? false}
          />
        )}

        {activeTab === 'roles' && (
          <RolesView
            teamId={team.id}
            roles={teamRoles}
            isAdmin={isUserAdmin ?? false}
            onRolesChange={loadTeamRoles}
          />
        )}
      </div>
    </div>
  );
}
